import { Box, Stack, Typography, Divider, Skeleton } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const LeadershipMilestones = ({ data, loading }) => {
	const hasData = Array.isArray(data) && data.length > 0;

	if (loading) {
		return (
			<Box
				sx={{
					flex: 1,
					p: "16px 18px",
					backgroundColor: "background.paper",
					border: "1px solid rgba(29,84,162,0.1)",
					borderRadius: "12px",
					boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
				}}
			>
				<Stack gap={0}>
					{[0, 1, 2].map((i) => (
						<Box key={i} width="100%">
							<Stack direction="row" alignItems="center" gap={1.25} py={0.5}>
								<Skeleton variant="circular" width={28} height={28} />
								<Box flex={1}>
									<Skeleton variant="text" width="50%" height={16} />
									<Skeleton variant="text" width="35%" height={14} />
								</Box>
								<Skeleton variant="text" width={40} height={18} />
							</Stack>
							{i < 2 && <Box sx={{ width: 1, height: 8, backgroundColor: "divider", ml: "13px" }} />}
						</Box>
					))}
				</Stack>

				<Box sx={{ my: 1.25 }}>
					<Divider />
				</Box>

				<Stack direction="row" justifyContent="space-between">
					<Skeleton variant="text" width="30%" />
					<Skeleton variant="text" width="20%" />
				</Stack>
			</Box>
		);
	}

	const milestones = Array.isArray(data) ? data : [];
	const totalEarned = milestones.filter((m) => Number(m.achieved)).reduce((sum, m) => sum + Number(m.reward ?? 0), 0);

	if (milestones.length === 0) {
		return (
			<Box
				sx={{
					flex: 1,
					p: "16px 18px",
					backgroundColor: "background.paper",
					border: "1px solid rgba(29,84,162,0.1)",
					borderRadius: "12px",
					boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					minHeight: 220,
				}}
			>
				<Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>This agent has no downlines</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				flex: 1,
				p: "16px 18px",
				backgroundColor: "background.paper",
				border: "1px solid rgba(29,84,162,0.1)",
				borderRadius: "12px",
				boxShadow: "0 1px 8px rgba(29,84,162,0.06)",
			}}
		>
			<Stack gap={0}>
				{milestones.map((milestone) => {
					const achieved = Number(milestone.achieved) === 1;
					const target = Number(milestone.target);
					const reward = Number(milestone.reward);
					const progress = Number(milestone.progress);

					const progressPercent = Math.min((progress / target) * 100, 100);

					return (
						<Box key={milestone.id} sx={{ mb: 1.5 }}>
							<Stack direction="row" alignItems="center" gap={1.25}>
								<Box
									sx={{
										width: 28,
										height: 28,
										borderRadius: "50%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										flexShrink: 0,
										backgroundColor: achieved ? "#EAF3DE" : "#EEF4FC",
										color: achieved ? "#3B6D11" : "#4F7DCB",
									}}
								>
									{achieved ? <CheckIcon sx={{ fontSize: 14 }} /> : <LockOutlinedIcon sx={{ fontSize: 14 }} />}
								</Box>

								<Box flex={1}>
									<Stack direction="row" justifyContent="space-between" alignItems="center">
										<Typography
											sx={{
												fontSize: "0.75rem",
												fontWeight: 500,
											}}
										>
											{target.toLocaleString()} members
										</Typography>

										<Typography
											sx={{
												fontSize: "0.8rem",
												fontWeight: 600,
												color: achieved ? "text.primary" : "text.secondary",
											}}
										>
											${reward.toLocaleString()}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.25} mb={0.5}>
										<Typography
											sx={{
												fontSize: "0.68rem",
												color: achieved ? "#3B6D11" : "text.secondary",
											}}
										>
											{progress.toLocaleString()} / {target.toLocaleString()}
										</Typography>

										<Typography
											sx={{
												fontSize: "0.68rem",
												fontWeight: 500,
												color: achieved ? "#3B6D11" : "#4F7DCB",
											}}
										>
											{Math.round(progressPercent)}%
										</Typography>
									</Stack>

									<Box
										sx={{
											height: 7,
											borderRadius: 999,
											backgroundColor: "#E5E7EB",
											overflow: "hidden",
										}}
									>
										<Box
											sx={{
												height: "100%",
												width: `${progressPercent}%`,
												borderRadius: 999,
												background: achieved
													? "linear-gradient(90deg, #8BC34A 0%, #6AA52C 100%)"
													: "linear-gradient(90deg, #4F7DCB 0%, #6EA3F2 100%)",
												transition: "width .4s ease",
											}}
										/>
									</Box>
								</Box>
							</Stack>
						</Box>
					);
				})}
			</Stack>

			<Box sx={{ my: 1.25 }}>
				<Divider />
			</Box>

			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>Total earned</Typography>

				<Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>${totalEarned.toLocaleString()}</Typography>
			</Stack>
		</Box>
	);
};

export default LeadershipMilestones;
